// Purpose: /shares/:id — single share detail.
// Phase 7.5 port: PageHeader + Card metadata blocks + Badge status +
// Button variants + Modal with Input.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  archiveShare,
  extendShare,
  getRecipientActivity,
  getShare,
  recoverRecipients,
  recoverShareLink,
  revokeRecipient,
  revokeShare,
  unarchiveShare,
  type CreatedRecipient,
  type RecipientActivityEntry,
  type ShareDetail as ShareDetailT,
  type ShareRecipientView,
} from '../api/shares.ts';
import { Breadcrumb, PageHeader } from '../components/nav/PageHeader.tsx';
import { Badge, Button, Card, Input, Modal } from '../components/ui/index.ts';
import {
  clearPendingRecoverLink,
  consumePendingRecoverLink,
  stashPendingRecoverLink,
} from '../lib/pendingActions.ts';

// Same ceiling as the server's AST_SHARE_MAX_TTL_HOURS default (168 /
// 7 days). Keep the client upper bound loose; the server rejects past
// the real configured ceiling.
const EXTEND_MAX_HOURS = 168;

function fmt(bytes: string): string {
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// datetime-local input wants "YYYY-MM-DDTHH:mm" in local time; Date
// formatters differ between browsers so build the string manually.
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function statusBadge(status: string) {
  if (status === 'REVOKED') return <Badge tone="danger">Revoked</Badge>;
  if (status === 'EXPIRED') return <Badge tone="neutral">Expired</Badge>;
  if (status === 'LIVE') return <Badge tone="info">Live</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

export function ShareDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [share, setShare] = useState<ShareDetailT | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendValue, setExtendValue] = useState<string>('');
  const [extending, setExtending] = useState(false);
  // Commit 8b.1: link recovery. The URL lives in component state only
  // for the life of the acknowledge-modal; never persisted, never
  // passed through a toast that auto-closes. The user clicks "Close"
  // explicitly to clear it.
  const [recoveringLink, setRecoveringLink] = useState(false);
  const [recoveredUrl, setRecoveredUrl] = useState<string | null>(null);
  const [recoverError, setRecoverError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Commit 8b.14: per-recipient ops + recover-recipients (returns full
  // per-recipient link+code list in one MFA round-trip).
  const [recoveringRecipients, setRecoveringRecipients] = useState(false);
  const [recoveredRecipients, setRecoveredRecipients] = useState<CreatedRecipient[] | null>(null);
  const [revokingRecipient, setRevokingRecipient] = useState<string | null>(null);
  // Commit 8b.16: archive/unarchive in-flight. Owner action, auth+CSRF
  // only (no MFA — visibility-organizational).
  const [archiving, setArchiving] = useState(false);

  async function handleToggleArchive() {
    if (!id || !share) return;
    setArchiving(true);
    setError(null);
    try {
      if (share.isArchived) await unarchiveShare(id);
      else await archiveShare(id);
      const fresh = await getShare(id);
      setShare(fresh);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setArchiving(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    getShare(id)
      .then(setShare)
      .catch((err) => setError((err as Error).message));
  }, [id]);

  // Commit 8b.6: auto-continue the Recover Link flow after the
  // operator bounces through /auth/step-up. onClick stashes an
  // intent in sessionStorage before posting to recover-link; on 428
  // the stash stays and the interceptor navigates to step-up; after
  // Entra + callback + 302 back to this detail page, consume() here
  // fires the recovery again — this time under fresh MFA. The
  // consume removes the stash, so a refresh-on-this-page won't
  // re-fire and 428 from the auto-fire won't infinite-loop (the
  // stash is gone; the manual click path is how the operator retries).
  useEffect(() => {
    if (!id) return;
    const pending = consumePendingRecoverLink('share', id);
    if (!pending) return;
    // Commit 8b.24 (P2): resume the action that was actually stashed. A
    // 'recipients' intent must NOT fall through to runRecoverLink (which
    // would show the share link instead of the per-recipient codes).
    if (pending.action === 'recipients') {
      void runRecoverRecipients({ autoContinue: true });
    } else {
      void runRecoverLink({ autoContinue: true });
    }
  }, [id]);

  async function runRecoverLink(opts: { autoContinue?: boolean } = {}): Promise<void> {
    if (!id) return;
    setRecoverError(null);
    setRecoveringLink(true);
    // Stash the intent before the POST so the 428 → /auth/step-up
    // redirect can survive the full-page nav and auto-continue on
    // return. Cleared on success / 409 / 429 / other errors.
    stashPendingRecoverLink('share', id);
    try {
      const { publicUrl } = await recoverShareLink(id);
      setRecoveredUrl(publicUrl);
      setCopied(false);
      clearPendingRecoverLink();
    } catch (err) {
      const ax = err as { response?: { status?: number; data?: { error?: string } } };
      // 428 → axios interceptor navigates to step-up; stash stays,
      // post-mount useEffect picks it up.
      if (ax.response?.status === 428) return;
      clearPendingRecoverLink();
      if (ax.response?.status === 409) {
        setRecoverError(
          'This share was created before link recovery was enabled. No recovery path — if the original link is lost, you will need to revoke and create a fresh share.'
        );
      } else if (ax.response?.status === 429) {
        setRecoverError(
          'Too many recovery attempts for this share (3/hour limit). Wait an hour or ask an admin.'
        );
      } else {
        setRecoverError(
          opts.autoContinue
            ? `Auto-continue after MFA failed: ${(err as Error).message}`
            : (err as Error).message
        );
      }
    } finally {
      setRecoveringLink(false);
    }
  }

  async function runRecoverRecipients(opts: { autoContinue?: boolean } = {}): Promise<void> {
    if (!id) return;
    setRecoverError(null);
    setRecoveringRecipients(true);
    // Stash with action 'recipients' so a 428 → step-up bounce resumes THIS
    // flow (per-recipient codes), not the link recovery (Commit 8b.24 P2).
    stashPendingRecoverLink('share', id, 'recipients');
    try {
      const { recipients } = await recoverRecipients(id);
      setRecoveredRecipients(recipients);
      clearPendingRecoverLink();
    } catch (err) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 428) return;
      clearPendingRecoverLink();
      setRecoverError(
        opts.autoContinue
          ? `Auto-continue after MFA failed: ${(err as Error).message}`
          : (err as Error).message
      );
    } finally {
      setRecoveringRecipients(false);
    }
  }

  async function handleRevokeRecipient(recipientId: string, email: string) {
    if (!id) return;
    if (!confirm(`Revoke access for ${email}? Their link and code stop working immediately.`))
      return;
    setRevokingRecipient(recipientId);
    try {
      await revokeRecipient(id, recipientId);
      const fresh = await getShare(id);
      setShare(fresh);
    } catch (err) {
      const ax = err as { response?: { status?: number } };
      if (ax.response?.status === 428) {
        // SPA's axios interceptor already navigated to step-up; they'll
        // need to re-click after returning. (Auto-continue is a future
        // niceness; for 8b.14 the manual second click is fine.)
        return;
      }
      setError((err as Error).message);
    } finally {
      setRevokingRecipient(null);
    }
  }

  if (error && !share) {
    return (
      <div
        role="alert"
        className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
      >
        {error}
      </div>
    );
  }
  if (!share) return <p className="text-body-m text-fg-muted">Loading…</p>;

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <PageHeader
        breadcrumb={
          <Breadcrumb
            items={[{ label: 'Shares', to: '/shares' }, { label: share.name ?? '(unnamed)' }]}
          />
        }
        title={share.name ?? '(unnamed)'}
        actions={
          <>
            {/* Commit 8b.16: Archive / Unarchive available regardless
                of lifecycle state (visibility-organizational, never
                blocks recipient access). */}
            <Button
              variant="secondary"
              size="md"
              loading={archiving}
              onClick={() => void handleToggleArchive()}
              data-testid="toggle-archive"
            >
              {share.isArchived ? 'Unarchive' : 'Archive'}
            </Button>
            {share.status === 'LIVE' ? (
              <>
                <Button
                  variant="secondary"
                  size="md"
                  loading={recoveringLink}
                  onClick={() => void runRecoverLink()}
                >
                  Recover link
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => {
                    const defaultAt = new Date(Date.now() + 24 * 3600 * 1000);
                    setExtendValue(toLocalInput(defaultAt));
                    setExtendOpen(true);
                  }}
                >
                  Extend expiry
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  loading={revoking}
                  onClick={async () => {
                    if (!id) return;
                    if (!confirm('Revoke this share? The link will stop working immediately.'))
                      return;
                    setRevoking(true);
                    try {
                      await revokeShare(id);
                      navigate('/shares');
                    } catch (err) {
                      setError((err as Error).message);
                      setRevoking(false);
                    }
                  }}
                >
                  Revoke share
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {error ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
        >
          {error}
        </div>
      ) : null}

      {recoverError ? (
        <div
          role="alert"
          className="px-4 py-3 rounded-md text-body-s border border-status-warning/30 bg-status-warning-muted text-status-warning"
        >
          {recoverError}
        </div>
      ) : null}

      <Card padding="comfortable">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-label uppercase text-fg-secondary mb-1">Status</dt>
            <dd>{statusBadge(share.status)}</dd>
          </div>
          <div>
            <dt className="text-label uppercase text-fg-secondary mb-1">Expires</dt>
            <dd className="text-body-m text-fg-primary">
              {share.expiresAt ? new Date(share.expiresAt).toLocaleString() : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-label uppercase text-fg-secondary mb-1">Size</dt>
            <dd className="text-body-m text-fg-primary">{fmt(share.sizeBytes)}</dd>
          </div>
          {share.recipientHint ? (
            <div>
              <dt className="text-label uppercase text-fg-secondary mb-1">Recipient hint</dt>
              <dd className="text-body-m text-fg-primary">{share.recipientHint}</dd>
            </div>
          ) : null}
          {share.accessCode ? (
            <div className="sm:col-span-2">
              <dt className="text-label uppercase text-fg-secondary mb-1">
                Access code <span className="text-caption text-fg-muted">(legacy share)</span>
              </dt>
              <dd>
                <code className="text-body-m font-mono tracking-widest text-fg-primary">
                  {share.accessCode}
                </code>
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      {/* Commit 8b.14: Recipients section. Always rendered when the share
          has any recipients (post-deploy: every backfilled and new share
          has at least one). */}
      {share.recipients && share.recipients.length > 0 ? (
        <Card padding="none">
          <div className="p-5 pb-3 flex items-center justify-between gap-4">
            <h2 className="text-heading-m text-fg-primary">
              Recipients ({share.recipients.length})
            </h2>
            {share.status === 'LIVE' ? (
              <Button
                variant="secondary"
                size="sm"
                loading={recoveringRecipients}
                onClick={() => void runRecoverRecipients()}
              >
                Recover codes
              </Button>
            ) : null}
          </div>
          <ul className="divide-y divide-border-subtle">
            {share.recipients.map((r) => (
              <RecipientRow
                key={r.id}
                recipient={r}
                shareId={share.id}
                shareLive={share.status === 'LIVE'}
                revoking={revokingRecipient === r.id}
                onRevoke={() => void handleRevokeRecipient(r.id, r.email)}
              />
            ))}
          </ul>
        </Card>
      ) : null}

      <Card padding="none">
        <div className="p-5 pb-3">
          <h2 className="text-heading-m text-fg-primary">Files</h2>
        </div>
        <ul className="divide-y divide-border-subtle">
          {share.files.map((f) => (
            <li key={f.id} className="px-5 py-3 flex items-center justify-between gap-4">
              <span className="text-body-m text-fg-primary truncate">{f.originalFilename}</span>
              <span className="text-body-s text-fg-muted flex-shrink-0">{fmt(f.sizeBytes)}</span>
            </li>
          ))}
        </ul>
      </Card>

      {/* Commit 8b.1: recovered-link modal. No auto-close — the
          operator must click Close after copying. On close the URL is
          dropped from component state; to see it again requires a
          second recovery (MFA + rate limit re-paid). */}
      <Modal
        open={recoveredUrl !== null}
        onClose={() => {
          setRecoveredUrl(null);
          setCopied(false);
        }}
        title="Recovered share link"
        size="md"
        footer={
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setRecoveredUrl(null);
                setCopied(false);
              }}
            >
              Close
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-body-m text-fg-secondary">
            Copy the URL now — closing this dialog clears it from the page. Recovering again
            requires another MFA step-up.
          </p>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 min-w-0 px-3 py-2 rounded-md bg-bg-tertiary text-body-s font-mono text-fg-primary break-all">
              {recoveredUrl}
            </code>
            <Button
              variant="secondary"
              size="sm"
              onClick={async () => {
                if (!recoveredUrl) return;
                try {
                  await navigator.clipboard.writeText(recoveredUrl);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Commit 8b.14: per-recipient code recovery modal. Same posture
          as the legacy recovered-link modal (no auto-close, drop on Close). */}
      <Modal
        open={recoveredRecipients !== null}
        onClose={() => setRecoveredRecipients(null)}
        title="Recovered recipient codes"
        size="lg"
        footer={
          <Button variant="primary" size="sm" onClick={() => setRecoveredRecipients(null)}>
            Close
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-body-m text-fg-secondary">
            Copy what you need now — closing this dialog drops the codes from the page. Recovering
            again requires another MFA step-up.
          </p>
          {recoveredRecipients?.map((r) => (
            <div key={r.recipientId} className="flex flex-col gap-2 p-3 rounded-md bg-bg-tertiary">
              <div className="text-label uppercase text-fg-secondary">{r.email}</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 min-w-0 px-2 py-1 rounded bg-surface-elevated text-body-s font-mono text-fg-primary break-all">
                  {r.link}
                </code>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => void navigator.clipboard.writeText(r.link)}
                >
                  Copy link
                </Button>
              </div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 px-2 py-1 rounded bg-surface-elevated text-body-m font-mono tracking-widest text-fg-primary">
                  {r.accessCode}
                </code>
                <Button
                  variant="subtle"
                  size="sm"
                  onClick={() => void navigator.clipboard.writeText(r.accessCode)}
                >
                  Copy code
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal
        open={extendOpen}
        onClose={() => setExtendOpen(false)}
        title="Extend share expiry"
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setExtendOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={extending}
              disabled={!extendValue}
              onClick={async () => {
                if (!id || !extendValue) return;
                setExtending(true);
                setError(null);
                try {
                  const iso = new Date(extendValue).toISOString();
                  await extendShare(id, iso);
                  setExtendOpen(false);
                  const fresh = await getShare(id);
                  setShare(fresh);
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  setExtending(false);
                }
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-body-m text-fg-secondary">
            Push this share&apos;s expiry forward. The server caps the new value at{' '}
            <strong className="text-fg-primary">{EXTEND_MAX_HOURS} hours from now</strong>; past
            that the save returns a 400.
          </p>
          <Input
            label="New expiry"
            type="datetime-local"
            value={extendValue}
            onChange={(e) => setExtendValue(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}

function recipientStatusBadge(status: ShareRecipientView['status']) {
  if (status === 'PENDING') return <Badge tone="neutral">Pending</Badge>;
  if (status === 'NOTIFIED') return <Badge tone="info">Notified</Badge>;
  if (status === 'EMAIL_VERIFIED') return <Badge tone="info">Email verified</Badge>;
  if (status === 'ACCESSED') return <Badge tone="success">Accessed</Badge>;
  if (status === 'REVOKED') return <Badge tone="danger">Revoked</Badge>;
  if (status === 'EXPIRED') return <Badge tone="neutral">Expired</Badge>;
  return <Badge tone="neutral">{status}</Badge>;
}

function RecipientRow({
  recipient,
  shareId,
  shareLive,
  revoking,
  onRevoke,
}: {
  recipient: ShareRecipientView;
  shareId: string;
  shareLive: boolean;
  revoking: boolean;
  onRevoke: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activity, setActivity] = useState<RecipientActivityEntry[] | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityError, setActivityError] = useState<string | null>(null);

  async function loadActivity() {
    setLoadingActivity(true);
    setActivityError(null);
    try {
      const { activity } = await getRecipientActivity(shareId, recipient.id);
      setActivity(activity);
    } catch (err) {
      setActivityError((err as Error).message);
    } finally {
      setLoadingActivity(false);
    }
  }

  function toggleExpanded() {
    if (!expanded && activity === null) void loadActivity();
    setExpanded((v) => !v);
  }

  return (
    <li className="px-5 py-3 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-body-m text-fg-primary truncate">{recipient.email}</div>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-caption text-fg-muted">
            {recipient.lastAccessedAt ? (
              <span>Last accessed {new Date(recipient.lastAccessedAt).toLocaleString()}</span>
            ) : recipient.notifiedAt ? (
              <span>Notified {new Date(recipient.notifiedAt).toLocaleString()}</span>
            ) : null}
            {recipient.failedAttempts > 0 ? (
              <span className="text-status-warning">
                {recipient.failedAttempts} failed attempt
                {recipient.failedAttempts === 1 ? '' : 's'}
              </span>
            ) : null}
            {recipient.lockedUntil ? (
              <span className="text-status-danger">
                Locked until {new Date(recipient.lockedUntil).toLocaleTimeString()}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {recipientStatusBadge(recipient.status)}
          <Button variant="subtle" size="sm" onClick={toggleExpanded}>
            {expanded ? 'Hide activity' : 'Activity'}
          </Button>
          {shareLive && recipient.status !== 'REVOKED' ? (
            <Button variant="subtle" size="sm" loading={revoking} onClick={onRevoke}>
              Revoke
            </Button>
          ) : null}
        </div>
      </div>
      {expanded ? (
        <RecipientActivityPanel
          loading={loadingActivity}
          error={activityError}
          activity={activity}
          email={recipient.email}
          onRefresh={() => void loadActivity()}
        />
      ) : null}
    </li>
  );
}

function RecipientActivityPanel({
  loading,
  error,
  activity,
  email,
  onRefresh,
}: {
  loading: boolean;
  error: string | null;
  activity: RecipientActivityEntry[] | null;
  email: string;
  onRefresh: () => void;
}) {
  function exportCsv() {
    if (!activity) return;
    const header = 'occurredAt,action,ipAddress,userAgent,metadata\n';
    const escape = (s: string): string =>
      s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    const rows = activity
      .map((row) =>
        [
          row.occurredAt,
          row.action,
          row.ipAddress ?? '',
          row.userAgent ?? '',
          row.metadata ? JSON.stringify(row.metadata) : '',
        ]
          .map(escape)
          .join(',')
      )
      .join('\n');
    void navigator.clipboard.writeText(header + rows);
  }

  if (loading)
    return (
      <div className="text-body-s text-fg-muted px-3 py-2 bg-bg-tertiary rounded">
        Loading activity…
      </div>
    );
  if (error)
    return (
      <div
        role="alert"
        className="px-3 py-2 rounded text-body-s border border-status-danger/30 bg-status-danger-muted text-status-danger"
      >
        {error}
      </div>
    );
  if (!activity) return null;
  if (activity.length === 0)
    return (
      <div className="text-body-s text-fg-muted px-3 py-2 bg-bg-tertiary rounded">
        No activity yet for {email}.
      </div>
    );
  return (
    <div className="px-3 py-2 bg-bg-tertiary rounded">
      <div className="flex items-center justify-between mb-2">
        <div className="text-caption uppercase text-fg-muted">
          {activity.length} event{activity.length === 1 ? '' : 's'}
        </div>
        <div className="flex gap-2">
          <Button variant="subtle" size="sm" onClick={onRefresh}>
            Refresh
          </Button>
          <Button variant="subtle" size="sm" onClick={exportCsv}>
            Copy CSV
          </Button>
        </div>
      </div>
      <ul className="divide-y divide-border-subtle">
        {activity.map((row) => (
          <li key={row.id} className="py-2">
            <div className="flex flex-wrap items-center gap-2 text-body-s">
              <span className="font-mono text-fg-primary">{row.action}</span>
              <span className="text-fg-muted">{new Date(row.occurredAt).toLocaleString()}</span>
              {row.ipAddress ? (
                <span className="font-mono text-fg-secondary">{row.ipAddress}</span>
              ) : null}
            </div>
            {row.userAgent ? (
              <div className="text-caption text-fg-muted truncate mt-0.5" title={row.userAgent}>
                {row.userAgent}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
